import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VpnProtocolIcon from "../../../components/VpnProtocolIcon";
import AppIconButton from "../../../components/form/AppIconButton";
import { AppAutocomplete, AppButton, AppTextField, AppToggleField } from "../../../components/form";
import { AppFormField, AppFormGrid, AppSlideOver, PageSectionCard } from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { GRID_FULL, GRID_HALF, formRowButtonSx } from "../../../lib/ui/formLayout";
import {
  VPN_PROTOCOLS,
  adminVpnProfileService,
  type VpnProfile,
  type VpnProfileSaveRequest,
  type VpnProfileStatus,
  type VpnProtocol,
} from "../../../services/adminVpnProfileService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { requestVpnOtp } from "../../../services/vpnOtpPrompt";

type ProtocolOption = { value: VpnProtocol; label: string };
type FormState = Omit<VpnProfileSaveRequest, "port"> & { id?: string; passwordSet?: boolean; port?: string };

const emptyForm = (): FormState => ({
  name: "",
  description: "",
  enabled: true,
  protocol: "ANYCONNECT",
  server: "",
  username: "",
  password: "",
  authGroup: "",
  serverCertPin: "",
  otpRequired: false,
  port: "",
  acceptUntrustedCertificate: false,
});

const protocolLabel = (protocol: VpnProtocol): string =>
  VPN_PROTOCOLS.find((option) => option.value === protocol)?.label ?? protocol;

function StatusChip({ status }: { status?: VpnProfileStatus }) {
  const { t } = useTranslation();
  if (status?.connected) {
    return <Chip size="small" color="success" label={t("vpnProfiles.statusConnected")} />;
  }
  if (status?.lastError) {
    return (
      <Chip size="small" color="error" variant="outlined" label={t("vpnProfiles.statusError")} title={status.lastError} />
    );
  }
  return <Chip size="small" variant="outlined" label={t("vpnProfiles.statusDisconnected")} />;
}

/** OpenConnect VPN definitions that data adaptors and OpenAPI catalogs can be assigned to. */
export default function AdminVpnProfilesPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [items, setItems] = useState<VpnProfile[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formOpen, setFormOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await adminVpnProfileService.list());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setForm(emptyForm());
    setError(null);
  };

  const startCreate = () => {
    setForm(emptyForm());
    setMessage(null);
    setError(null);
    setFormOpen(true);
  };

  const edit = (item: VpnProfile) => {
    setForm({
      id: item.id,
      name: item.name,
      description: item.description ?? "",
      enabled: item.enabled,
      protocol: item.protocol,
      server: item.server,
      username: item.username,
      password: "",
      authGroup: item.authGroup ?? "",
      serverCertPin: item.serverCertPin ?? "",
      otpRequired: !!item.otpRequired,
      port: item.port ? String(item.port) : "",
      acceptUntrustedCertificate: !!item.acceptUntrustedCertificate,
      passwordSet: item.passwordSet,
    });
    setMessage(null);
    setError(null);
    setFormOpen(true);
  };

  const run = async (action: () => Promise<string | void>) => {
    setWorking(true);
    setError(null);
    setMessage(null);
    try {
      const result = await action();
      if (result) setMessage(result);
      await load();
    } catch (e: unknown) {
      if (!isCancelled(e)) {
        const backendMessage = (e as { response?: { data?: { response?: { message?: string } } } })?.response?.data
          ?.response?.message;
        setError(backendMessage || (e instanceof Error ? e.message : String(e)));
      }
    } finally {
      setWorking(false);
    }
  };

  const save = () =>
    run(async () => {
      const body: VpnProfileSaveRequest = {
        name: form.name?.trim(),
        description: form.description?.trim() ?? "",
        enabled: form.enabled,
        protocol: form.protocol,
        server: form.server?.trim(),
        username: form.username?.trim(),
        authGroup: form.authGroup?.trim() ?? "",
        serverCertPin: form.serverCertPin?.trim() ?? "",
        otpRequired: !!form.otpRequired,
        port: form.port?.trim() ? Number(form.port) : 0,
        acceptUntrustedCertificate: !!form.acceptUntrustedCertificate,
      };
      if (form.password) body.password = form.password;
      if (form.id) {
        await adminVpnProfileService.update(form.id, body);
        setFormOpen(false);
        setForm(emptyForm());
        return t("vpnProfiles.updated");
      }
      await adminVpnProfileService.create(body);
      setFormOpen(false);
      setForm(emptyForm());
      return t("vpnProfiles.created");
    });

  const remove = (item: VpnProfile) =>
    run(async () => {
      await confirmDelete({ itemName: item.name });
      await adminVpnProfileService.remove(item.id);
      if (form.id === item.id) {
        setFormOpen(false);
        setForm(emptyForm());
      }
    });

  const connect = async (item: VpnProfile) => {
    if (!item.enabled || item.status?.connected) return;
    setConnectingId(item.id);
    setError(null);
    setMessage(null);
    try {
      if (item.otpRequired) {
        const connected = await requestVpnOtp({ vpnProfileId: item.id, vpnProfileName: item.name });
        if (connected) setMessage(t("vpnProfiles.connectedWithOtp"));
      } else {
        const result = await adminVpnProfileService.test(item.id);
        setMessage(result.message || t("vpnProfiles.testOk"));
      }
      await load();
    } catch (e: unknown) {
      const backendMessage = (e as { response?: { data?: { response?: { message?: string } } } })?.response?.data
        ?.response?.message;
      setError(backendMessage || (e instanceof Error ? e.message : String(e)));
    } finally {
      setConnectingId(null);
    }
  };

  const disconnect = async (item: VpnProfile) => {
    if (!item.status?.connected) return;
    setConnectingId(item.id);
    setError(null);
    setMessage(null);
    try {
      await adminVpnProfileService.disconnect(item.id);
      setMessage(t("vpnProfiles.disconnected"));
      await load();
    } catch (e: unknown) {
      const backendMessage = (e as { response?: { data?: { response?: { message?: string } } } })?.response?.data
        ?.response?.message;
      setError(backendMessage || (e instanceof Error ? e.message : String(e)));
    } finally {
      setConnectingId(null);
    }
  };

  const usageCount = (item: VpnProfile) => (item.dataAdaptors?.length ?? 0) + (item.openApiCatalogs?.length ?? 0);

  const rowActions = (item: VpnProfile) => {
    const busy = connectingId === item.id || working;
    const connected = !!item.status?.connected;
    return (
      <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center" onClick={(e) => e.stopPropagation()}>
        {connected ? (
          <AppButton size="small" variant="outlined" disabled={busy} onClick={() => void disconnect(item)}>
            {busy ? t("common.loading") : t("vpnProfiles.disconnect")}
          </AppButton>
        ) : (
          <AppButton
            size="small"
            variant="contained"
            disabled={busy || !item.enabled}
            onClick={() => void connect(item)}
          >
            {busy ? t("common.loading") : t("vpnOtp.connect")}
          </AppButton>
        )}
        <AppIconButton size="small" aria-label={t("common.edit")} onClick={() => edit(item)}>
          <EditOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton
          size="small"
          aria-label={t("common.delete")}
          sx={formRowButtonSx}
          disabled={usageCount(item) > 0}
          title={usageCount(item) > 0 ? t("vpnProfiles.inUseHint") : undefined}
          onClick={() => void remove(item)}
        >
          <DeleteOutlineOutlinedIcon fontSize="small" />
        </AppIconButton>
      </Stack>
    );
  };

  const protocol = (form.protocol ?? "ANYCONNECT") as VpnProtocol;

  return (
    <PageListContainer>
      <PageHeader
        title={t("vpnProfiles.title")}
        subtitle={t("vpnProfiles.subtitle")}
        actions={
          <AppButton variant="contained" startIcon={<AddOutlinedIcon />} onClick={startCreate} disabled={working}>
            {t("vpnProfiles.newProfile")}
          </AppButton>
        }
      />

      {error && !formOpen && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <PageSectionCard title={t("vpnProfiles.listTitle")} noPadding>
        {loading ? (
          <Stack alignItems="center" py={5}>
            <CircularProgress size={32} />
          </Stack>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <PageEmptyState
              message={t("vpnProfiles.empty")}
              actionLabel={t("vpnProfiles.newProfile")}
              onAction={startCreate}
              actionIcon={<AddOutlinedIcon />}
            />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ display: { xs: "none", sm: "block" }, maxWidth: "100%" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>{t("vpnProfiles.name")}</TableCell>
                    <TableCell>{t("vpnProfiles.protocol")}</TableCell>
                    <TableCell>{t("vpnProfiles.status")}</TableCell>
                    <TableCell>{t("vpnProfiles.usage")}</TableCell>
                    <TableCell align="right">{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover onClick={() => edit(item)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ width: 56, py: 1 }}>
                        <VpnProtocolIcon protocol={item.protocol} size={34} label={protocolLabel(item.protocol)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {item.server}
                          {item.otpRequired ? " · OTP" : ""}
                        </Typography>
                        {!item.enabled && (
                          <Chip
                            size="small"
                            label={`${t("vpnProfiles.enabled")}: ${t("common.no")}`}
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{protocolLabel(item.protocol)}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={item.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{usageCount(item)}</Typography>
                      </TableCell>
                      <TableCell align="right">{rowActions(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" }, p: 2 }}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => edit(item)}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                      <VpnProtocolIcon protocol={item.protocol} size={34} label={protocolLabel(item.protocol)} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {protocolLabel(item.protocol)} · {item.server}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <StatusChip status={item.status} />
                        </Box>
                      </Box>
                    </Stack>
                    {rowActions(item)}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </PageSectionCard>

      <AppSlideOver
        open={formOpen}
        onClose={closeForm}
        size="lg"
        title={form.id ? t("vpnProfiles.edit") : t("vpnProfiles.create")}
        actions={
          <>
            <AppButton variant="outlined" onClick={closeForm} disabled={working}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void save()} disabled={working}>
              {form.id ? t("common.save") : t("common.create")}
            </AppButton>
          </>
        }
      >
          {error && formOpen && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2.5}>
            <AppFormGrid>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.name")}
                  value={form.name ?? ""}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppToggleField
                  label={t("vpnProfiles.enabled")}
                  value={form.enabled ? "true" : "false"}
                  onChange={(v) => setForm({ ...form, enabled: v === "true" })}
                  options={[
                    { value: "true", label: t("common.yes") },
                    { value: "false", label: t("common.no") },
                  ]}
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("vpnProfiles.description")}
                  value={form.description ?? ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <VpnProtocolIcon protocol={protocol} size={36} label={protocolLabel(protocol)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AppAutocomplete<ProtocolOption>
                      label={t("vpnProfiles.protocol")}
                      options={VPN_PROTOCOLS}
                      value={VPN_PROTOCOLS.find((option) => option.value === protocol) ?? VPN_PROTOCOLS[0]}
                      disableClearable
                      autoHighlight
                      openOnFocus
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <VpnProtocolIcon protocol={option.value} size={28} label={option.label} />
                            <span>{option.label}</span>
                          </Stack>
                        </li>
                      )}
                      onChange={(_, option) => {
                        if (option) setForm({ ...form, protocol: option.value });
                      }}
                    />
                  </Box>
                </Stack>
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.server")}
                  value={form.server ?? ""}
                  onChange={(e) => setForm({ ...form, server: e.target.value })}
                  helperText={t("vpnProfiles.serverHelp")}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.port")}
                  type="number"
                  value={form.port ?? ""}
                  onChange={(e) => setForm({ ...form, port: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })}
                  inputProps={{ min: 1, max: 65535 }}
                  helperText={t("vpnProfiles.portHelp")}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.username")}
                  value={form.username ?? ""}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="off"
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.password")}
                  type="password"
                  autoComplete="new-password"
                  value={form.password ?? ""}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  helperText={form.id && form.passwordSet ? t("vpnProfiles.secretKeepHint") : undefined}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.authGroup")}
                  value={form.authGroup ?? ""}
                  onChange={(e) => setForm({ ...form, authGroup: e.target.value })}
                  helperText={t("vpnProfiles.optional")}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("vpnProfiles.serverCertPin")}
                  value={form.serverCertPin ?? ""}
                  onChange={(e) => setForm({ ...form, serverCertPin: e.target.value })}
                  helperText={t("vpnProfiles.serverCertPinHelp")}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppToggleField
                  label={t("vpnProfiles.acceptUntrustedCertificate")}
                  value={form.acceptUntrustedCertificate ? "true" : "false"}
                  onChange={(v) => setForm({ ...form, acceptUntrustedCertificate: v === "true" })}
                  options={[
                    { value: "false", label: t("common.no") },
                    { value: "true", label: t("common.yes") },
                  ]}
                />
                {form.acceptUntrustedCertificate ? (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    {t("vpnProfiles.acceptUntrustedCertificateWarning")}
                  </Alert>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {t("vpnProfiles.encryptionNote")}
                  </Typography>
                )}
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppToggleField
                  label={t("vpnProfiles.otpRequired")}
                  value={form.otpRequired ? "true" : "false"}
                  onChange={(v) => setForm({ ...form, otpRequired: v === "true" })}
                  options={[
                    { value: "false", label: t("common.no") },
                    { value: "true", label: t("common.yes") },
                  ]}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {t("vpnProfiles.otpRequiredHelp")}
                </Typography>
              </AppFormField>
            </AppFormGrid>

            <Alert severity="info" variant="outlined">
              {t("vpnProfiles.autoConnectInfo")}
            </Alert>
          </Stack>
      </AppSlideOver>
    </PageListContainer>
  );
}
